/**
 * PaymentTransaction withdrawal statuses — keep in sync with backend ENUM:
 * `payment_transactions.status` in PaymentTransaction model.
 */
export const WITHDRAWAL_PAYMENT_STATUSES = [
  'pending',
  'processing',
  'approved',
  'awaiting_admin',
  'retry_scheduled',
  'completed',
  'failed',
  'rejected',
  'cancelled',
  'expired',
  'partial',
  'refunded',
] as const;

export type WithdrawalPaymentStatus = (typeof WITHDRAWAL_PAYMENT_STATUSES)[number];

export type WithdrawalStatusTone = 'warning' | 'info' | 'success' | 'danger' | 'neutral';

export type WithdrawalStatusDisplay = {
  /** Raw status from API (may be unknown on older rows). */
  status: string;
  label: string;
  className: string;
  tone: WithdrawalStatusTone;
};

/** Derived admin-only badge — not stored on payment_transactions.status. */
const VIRTUAL_STATUS_DISPLAY: Record<string, Omit<WithdrawalStatusDisplay, 'status'>> = {
  failed_refunded: {
    label: 'Failed · Refunded',
    className: 'bg-emerald-100 text-emerald-900',
    tone: 'success',
  },
};

const STATUS_DISPLAY: Record<WithdrawalPaymentStatus, Omit<WithdrawalStatusDisplay, 'status'>> = {
  pending: {
    label: 'Pending',
    className: 'bg-yellow-100 text-yellow-800',
    tone: 'warning',
  },
  processing: {
    label: 'Processing',
    className: 'bg-blue-100 text-blue-800',
    tone: 'info',
  },
  approved: {
    label: 'Approved',
    className: 'bg-blue-100 text-blue-800',
    tone: 'info',
  },
  awaiting_admin: {
    label: 'Awaiting Review',
    className: 'bg-orange-100 text-orange-800',
    tone: 'warning',
  },
  retry_scheduled: {
    label: 'Retry Scheduled',
    className: 'bg-purple-100 text-purple-800',
    tone: 'info',
  },
  completed: {
    label: 'Completed',
    className: 'bg-green-100 text-green-800',
    tone: 'success',
  },
  failed: {
    label: 'Failed',
    className: 'bg-red-100 text-red-800',
    tone: 'danger',
  },
  rejected: {
    label: 'Rejected',
    className: 'bg-red-100 text-red-800',
    tone: 'danger',
  },
  cancelled: {
    label: 'Cancelled',
    className: 'bg-gray-100 text-gray-800',
    tone: 'neutral',
  },
  expired: {
    label: 'Expired',
    className: 'bg-gray-100 text-gray-800',
    tone: 'neutral',
  },
  partial: {
    label: 'Partial',
    className: 'bg-amber-100 text-amber-900',
    tone: 'warning',
  },
  refunded: {
    label: 'Refunded',
    className: 'bg-slate-100 text-slate-800',
    tone: 'neutral',
  },
};

function humanizeUnknownStatus(status: string): string {
  return status
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

export function getWithdrawalStatusDisplay(status: string | null | undefined): WithdrawalStatusDisplay {
  const raw = (status || '').trim();
  if (raw && raw in VIRTUAL_STATUS_DISPLAY) {
    return { status: raw, ...VIRTUAL_STATUS_DISPLAY[raw] };
  }
  const known = raw as WithdrawalPaymentStatus;
  if (raw && raw in STATUS_DISPLAY) {
    return { status: raw, ...STATUS_DISPLAY[known] };
  }
  return {
    status: raw || 'unknown',
    label: raw ? humanizeUnknownStatus(raw) : 'Unknown',
    className: 'bg-gray-100 text-gray-700',
    tone: 'neutral',
  };
}

export function isWithdrawalPaymentStatus(value: string): value is WithdrawalPaymentStatus {
  return (WITHDRAWAL_PAYMENT_STATUSES as readonly string[]).includes(value);
}

/** Statuses where admin can Approve/Reject from the withdrawal list (matches backend). */
export function isWithdrawalAdminApprovable(status: string | null | undefined): boolean {
  return status === 'pending' || status === 'awaiting_admin';
}

export function isWithdrawalPhase3RecoveryPending(withdrawal: {
  status?: string | null;
  phase3RecoveryPending?: boolean;
  syncMetadata?: { recoveryReason?: string | null; needsRecovery?: boolean | null } | null;
} | null | undefined): boolean {
  if (!withdrawal) return false;
  if (withdrawal.phase3RecoveryPending === true) return true;
  const meta = withdrawal.syncMetadata || {};
  return withdrawal.status === 'awaiting_admin'
    && meta.recoveryReason === 'phase3_withdrawal_failed'
    && meta.needsRecovery === true;
}

export function isWithdrawalAdminRecoverable(withdrawal: {
  status?: string | null;
  phase3RecoveryPending?: boolean;
  syncMetadata?: { recoveryReason?: string | null; needsRecovery?: boolean | null } | null;
} | null | undefined): boolean {
  return isWithdrawalPhase3RecoveryPending(withdrawal);
}

export function getWithdrawalApprovalOwnerLabel(
  status: string | null | undefined,
  options: { shopRestricted: boolean; approvedByEmail?: string | null }
): string {
  if (options.shopRestricted) return 'Shop (approval required)';
  if (options.approvedByEmail?.trim()) return options.approvedByEmail.trim();
  if (isWithdrawalAdminApprovable(status)) return 'Admin';
  if (status === 'processing' || status === 'approved' || status === 'retry_scheduled') {
    return 'Payment gateway';
  }
  return '—';
}

export function getWithdrawalAdminNextStep(
  status: string | null | undefined,
  shopRestricted: boolean,
  options?: { phase3RecoveryPending?: boolean }
): string {
  if (options?.phase3RecoveryPending) {
    return 'Provider debit succeeded but local settlement failed after retries. Use Complete Recovery to credit the master wallet and finalize the ledger.';
  }
  if (shopRestricted) {
    return 'Review request details only. Approval and rejection must be handled by the owning shop.';
  }
  switch (status) {
    case 'pending':
    case 'awaiting_admin':
      return 'Verify the requester and payout destination, then approve or reject using the list actions.';
    case 'processing':
      return 'Payout is in progress at the payment gateway. Monitor integration logs; approve/reject is not available for this status.';
    case 'approved':
    case 'retry_scheduled':
      return 'Payout is processing. If the gateway failed, use Retry Payout below or check integration logs.';
    case 'completed':
      return 'No action required — this withdrawal has been completed.';
    case 'rejected':
      return 'No action required — this request was rejected.';
    case 'failed':
      return 'Review the failure reason below and coordinate with operations or the requester if needed.';
    case 'cancelled':
    case 'expired':
      return 'No action required — this request is closed.';
    default:
      return 'Review request details and take action from the list when appropriate.';
  }
}

export const WITHDRAWAL_STATUS_FILTER_OPTIONS: { value: 'all' | WithdrawalPaymentStatus; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'awaiting_admin', label: 'Awaiting Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'retry_scheduled', label: 'Retry Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'expired', label: 'Expired' },
];
